import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { LeadDetail as LeadDetailType, LeadActionType } from '@nordcrest/shared';
import * as api from '../../lib/api';

export function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const [lead, setLead] = useState<LeadDetailType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionType, setActionType] = useState<LeadActionType>('note_added');
  const [actionNotes, setActionNotes] = useState('');
  const [isLoggingAction, setIsLoggingAction] = useState(false);

  useEffect(() => {
    if (id) {
      loadLead();
    }
  }, [id]);

  const loadLead = async () => {
    try {
      const data = await api.getLeadDetail(id!);
      setLead(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lead');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogAction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setIsLoggingAction(true);
    try {
      await api.logLeadAction(id, actionType, actionNotes || undefined);
      setActionNotes('');
      loadLead();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to log action');
    } finally {
      setIsLoggingAction(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('sv-SE');
  };

  const getTemperatureBadge = (temperature: string) => {
    const classes = {
      hot: 'badge-hot',
      warm: 'badge-warm',
      cold: 'badge-cold',
    };
    return (
      <span className={`badge ${classes[temperature as keyof typeof classes] || 'badge-cold'}`}>
        {temperature}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">{error || 'Lead not found'}</p>
        <Link to="/leads" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to leads
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link to="/leads" className="text-sm text-gray-500 hover:text-gray-700 mb-2 inline-block">
            ← Back to leads
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {lead.respondent.firstName} {lead.respondent.lastName}
          </h1>
          <p className="text-gray-500">{lead.respondent.email}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-gray-500">Lead Score</p>
            <p className="text-3xl font-bold text-gray-900">{lead.respondent.leadScore}</p>
          </div>
          {getTemperatureBadge(lead.respondent.leadTemperature)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Info */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-gray-500">Company</dt>
                <dd className="font-medium">{lead.respondent.company || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Role</dt>
                <dd className="font-medium">{lead.respondent.role || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Wants Callback</dt>
                <dd className="font-medium">
                  {lead.respondent.wantsCallback ? (
                    <span className="text-green-600">Yes</span>
                  ) : (
                    <span className="text-gray-400">No</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Created</dt>
                <dd className="font-medium">{formatDate(lead.respondent.createdAt)}</dd>
              </div>
            </dl>
          </div>

          {/* Survey Answers */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Survey Answers</h2>
            <div className="space-y-4">
              {lead.answers.map((answer) => (
                <div key={answer.questionId} className="border-b border-gray-100 pb-4 last:border-0">
                  <p className="text-sm text-gray-500">{answer.questionId}</p>
                  <p className="font-medium">
                    {Array.isArray(answer.value)
                      ? answer.value.join(', ')
                      : String(answer.value ?? '-')}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* AI Follow-ups */}
          {lead.aiFollowUps.length > 0 && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">AI Follow-up Answers</h2>
              <div className="space-y-4">
                {lead.aiFollowUps.map((followUp) => (
                  <div key={followUp.questionId} className="border-b border-gray-100 pb-4 last:border-0">
                    <p className="text-sm text-gray-500">{followUp.questionText}</p>
                    <p className="font-medium">
                      {Array.isArray(followUp.value)
                        ? followUp.value.join(', ')
                        : String(followUp.value ?? '-')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Report */}
          {lead.report && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Maturity Scores</h2>
              <div className="grid grid-cols-3 gap-4">
                {Object.entries(lead.report.maturityScores).map(([key, value]) => (
                  <div key={key} className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-500 capitalize">{key}</p>
                    <p className="text-xl font-bold text-gray-900">{value}/10</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Log Action */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Log Action</h2>
            <form onSubmit={handleLogAction} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Action Type
                </label>
                <select
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value as LeadActionType)}
                  className="input"
                >
                  <option value="note_added">Note Added</option>
                  <option value="email_sent">Email Sent</option>
                  <option value="call_attempted">Call Attempted</option>
                  <option value="call_completed">Call Completed</option>
                  <option value="meeting_scheduled">Meeting Scheduled</option>
                  <option value="proposal_sent">Proposal Sent</option>
                  <option value="converted">Converted</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  rows={3}
                  className="input"
                />
              </div>
              <button
                type="submit"
                disabled={isLoggingAction}
                className="btn btn-primary w-full"
              >
                {isLoggingAction ? 'Saving...' : 'Log Action'}
              </button>
            </form>
          </div>

          {/* Action History */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Action History</h2>
            {lead.actions.length === 0 ? (
              <p className="text-gray-500 text-sm">No actions logged yet</p>
            ) : (
              <div className="space-y-4">
                {lead.actions.map((action) => (
                  <div key={action.id} className="border-b border-gray-100 pb-4 last:border-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">
                        {action.actionType.replace('_', ' ')}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDate(action.createdAt)}
                      </span>
                    </div>
                    {action.notes && (
                      <p className="text-sm text-gray-600 mt-1">{action.notes}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">by {action.performedBy}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
